import {
  Clock, CheckCircle2, Send, MailOpen, Download, XCircle, RefreshCw, Bell,
  FileSignature, Users, AlertTriangle, FilePlus2, Ban, Link2,
} from "lucide-react";
import { gbp } from "@/lib/api";

// Friendly label + icon + colour per activity event.
const EVENT_META = {
  created: { label: "Draft created", icon: FilePlus2, tone: "slate" },
  sent: { label: "Proposal sent", icon: Send, tone: "teal" },
  link_created: { label: "Secure link created (not emailed)", icon: Link2, tone: "slate" },
  first_viewed: { label: "Opened by client", icon: MailOpen, tone: "teal" },
  pdf_downloaded: { label: "PDF downloaded", icon: Download, tone: "slate" },
  reminder_sent: { label: "Reminder sent", icon: Bell, tone: "amber" },
  signer_reminder_sent: { label: "Signatory reminder sent", icon: Bell, tone: "amber" },
  signer_resent: { label: "Link re-sent to a signatory", icon: RefreshCw, tone: "amber" },
  signers_chased: { label: "All outstanding signatories chased", icon: Bell, tone: "amber" },
  signer_signed: { label: "Signatory signed", icon: FileSignature, tone: "green" },
  accepted_signed: { label: "Accepted & signed", icon: CheckCircle2, tone: "green" },
  accepted_manual: { label: "Marked accepted (manual)", icon: CheckCircle2, tone: "green" },
  voided_reissued: { label: "Voided & re-issued", icon: Ban, tone: "red" },
  acceptance_reversed: { label: "Acceptance reversed", icon: RefreshCw, tone: "red" },
  rejected: { label: "Declined by client", icon: XCircle, tone: "red" },
  expired: { label: "Proposal expired", icon: AlertTriangle, tone: "red" },
  owner_escalated: { label: "Escalated to owner", icon: AlertTriangle, tone: "amber" },
  owner_notified_declined: { label: "Owner notified of decline", icon: XCircle, tone: "red" },
  revised: { label: "Revised — new version", icon: RefreshCw, tone: "amber" },
  engager_setup_created: { label: "Engager setup created", icon: Users, tone: "teal" },
  engager_setup_completed: { label: "Engager setup completed", icon: CheckCircle2, tone: "green" },
  onboarding_notification_sent: { label: "Onboarding notification sent", icon: Send, tone: "teal" },
  onboarding_notification_resent: { label: "Onboarding notification re-sent", icon: RefreshCw, tone: "amber" },
};

const TONE = {
  slate: "bg-cass-slate/15 text-cass-slate",
  teal: "bg-cass-teal/15 text-cass-teal",
  amber: "bg-amber-100 text-amber-600",
  green: "bg-emerald-100 text-emerald-600",
  red: "bg-red-100 text-red-600",
};

const meta = (ev) => EVENT_META[ev] || (ev.startsWith("email_")
  ? { label: ev.replace(/_/g, " "), icon: MailOpen, tone: "teal" }
  : { label: ev.replace(/_/g, " "), icon: Clock, tone: "slate" });

export default function ActivityTimeline({ activity = [] }) {
  const items = [...activity].sort((a, b) => new Date(b.at) - new Date(a.at));
  if (!items.length) return <p className="text-sm text-cass-slate">No activity yet.</p>;
  return (
    <div className="relative pl-2" data-testid="activity-timeline">
      <div className="absolute left-[15px] top-1 bottom-1 w-px bg-border" />
      <div className="space-y-4">
        {items.map((a, i) => {
          const m = meta(a.event);
          const Icon = m.icon;
          return (
            <div key={i} className="relative flex items-start gap-3" data-testid={`timeline-item-${a.event}`}>
              <div className={`z-10 shrink-0 w-7 h-7 rounded-full grid place-items-center ${TONE[m.tone]}`}>
                <Icon className="w-3.5 h-3.5" />
              </div>
              <div className="pt-0.5">
                <div className="text-sm text-cass-navy font-medium">{m.label}</div>
                <div className="text-xs text-cass-slate" title={new Date(a.at).toLocaleString("en-GB", { weekday: "long", day: "2-digit", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit", second: "2-digit" })}>
                  {new Date(a.at).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "2-digit", hour: "2-digit", minute: "2-digit" })}
                  {a.by ? ` · ${a.by}` : ""}
                </div>
                {a.note ? <div className="text-xs text-cass-slate/70 italic">{a.note}</div> : null}
                {a.from_value ? (
                  <div className="text-xs text-cass-slate/80" data-testid={`timeline-value-v${a.from_version}`}>
                    Value of v{a.from_version}: <span className="font-medium text-cass-navy">{gbp(a.from_value.monthly)}/mo</span> · {gbp(a.from_value.annual)}/yr{a.from_value.one_off ? ` · ${gbp(a.from_value.one_off)} one-off` : ""}
                  </div>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
